import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { DocumentKind } from "@steelbill/types";
import { api } from "@/lib/api";
import {
  endpoints,
  type ChallanListResponse,
  type CreditNoteListResponse,
  type CustomerListResponse,
  type InvoiceListResponse,
  type ItemListResponse,
  type QuotationListResponse,
} from "@/lib/endpoints";

/**
 * One box that searches the whole system (§11.2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SIX REGISTERS, ONE QUERY, IN PARALLEL. Invoices and quotations match on their
 * document numbers and reference fields; challans on their number and the
 * VEHICLE number (§17.7 — at the gate the paper is with the driver, so the lorry
 * number is often the only reference anyone has); credit notes on their own
 * number AND the bill they reduce (§34's link, so a BILL number finds them);
 * customers on name, phone and GSTIN; items on name, description and HSN code.
 * Every one of those is a SERVER search, so a hit on page nine of the register
 * is found — which is the whole difference between search and filtering what
 * happens to be on screen.
 *
 * NEITHER DOCUMENT SEARCH MATCHES THE CUSTOMER NAME, and that surprises people
 * until they see why: the buyer on an invoice lives in `customer_snapshot`, a
 * text-backed JSON column, and CLAUDE.md forbids querying inside one. Typing
 * "Lupin" therefore finds the CUSTOMER, and their documents are one tap further
 * on. That is a better answer anyway than a `LIKE` against serialised JSON,
 * which would also match an address or a GSTIN that merely contained the text.
 *
 * DEBOUNCED, AND NOT AT ALL BELOW TWO CHARACTERS. Every keystroke would
 * otherwise be five requests, and a single letter matches most of the book —
 * a list of everything is not an answer.
 *
 * ONE FAILURE DOES NOT SINK THE REST. Each request catches its own error, so if
 * the items endpoint is unavailable the customer results still appear. A search
 * box that goes blank because one of five calls failed looks like "nothing
 * matches", which is a lie.
 *
 * AND THAT IS ALSO HOW THE `challans` FLAG IS HANDLED — with nothing. Every
 * challan route is behind `requireFlag("challans")`, which runs before
 * `requireCap` and answers **404, not 403** (§18.3), so a build with the module
 * switched off rejects that one request and the per-request `.catch(() => null)`
 * above already swallows it: the section simply has no rows and is not drawn.
 * There is deliberately NO `useFlag` on the client and this hook does not invent
 * one — `GET /flags` needs `settings.write`, which the OWNER does not hold, so
 * asking would 403 for the one person using the app (the same reasoning as
 * `features/payments/use-payments.ts`).
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** Shortest query worth sending. One character matches nearly everything. */
export const MIN_SEARCH_LENGTH = 2;

export interface SearchHit {
  /**
   * EVERY DOCUMENT KIND, plus the two things that are not documents.
   *
   * Spelled as `DocumentKind | "customer" | "item"` rather than listing five
   * strings: this palette is the ONE surface that was updated when challans
   * shipped while three others beside it were not, and the reason it stayed
   * right was somebody remembering. §17.3's credit note now breaks the build
   * here — in `GlobalSearch.tsx`'s section list — instead of relying on that
   * again. A customer and an item are deliberately not `DocumentKind`: neither
   * is a document, and neither has a register, a number or a status.
   */
  kind: DocumentKind | "customer" | "item";
  id: number;
  /** What to show: an invoice number, a customer's name, a job name. */
  title: string;
  /** The second line — the customer, the state, the rate. */
  subtitle: string;
}

export interface GlobalSearchResults {
  invoices: SearchHit[];
  quotations: SearchHit[];
  challans: SearchHit[];
  /**
   * §17.3. THE ONE REGISTER WHERE `q` MATCHES ANOTHER DOCUMENT'S NUMBER:
   * `GET /credit-notes?q=` searches `credit_note_number` AND the frozen
   * `original_invoice_number`, so typing a BILL number surfaces every credit
   * note raised against it — in its own section, under the bill itself.
   */
  creditNotes: SearchHit[];
  customers: SearchHit[];
  items: SearchHit[];
  /** True while a query is in flight for text the user has already typed. */
  searching: boolean;
  /**
   * The text these results were actually fetched for — the raw query AFTER the
   * debounce below has caught up with it.
   *
   * IT IS RETURNED SO THE PALETTE'S OWN STATIC LISTS CAN FILTER ON THE SAME
   * TEXT. "Go to" and "Create" are filtered in `GlobalSearch.tsx` rather than by
   * cmdk (see the header there), and doing that against the raw box would make
   * the navigation entries snap a quarter-second ahead of the rows above them.
   * One timer, one answer to "what are we showing results for", rather than two
   * that can disagree by a tick.
   */
  debouncedQuery: string;
}

export function useDebounced(value: string, delayMs = 250): string {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value, delayMs]);

  return debounced;
}

export function useGlobalSearch(rawQuery: string): GlobalSearchResults {
  const raw = rawQuery.trim();
  const query = useDebounced(raw);
  const enabled = query.length >= MIN_SEARCH_LENGTH;

  /*
   * Has the debounce caught up with what has been typed?
   *
   * THIS EXISTS BECAUSE OF A 250ms LIE. `searching` used to be
   * `enabled && isFetching`, both of which read the DEBOUNCED query — so in the
   * window between typing the second character and the debounce firing, nothing
   * was enabled, nothing was fetching, and no results were in yet. The palette
   * concluded "no hits, not searching" and said **"Nothing matches that."**
   * about a name it had not yet looked up. It corrected itself a quarter-second
   * later, which is exactly long enough to read.
   */
  const settled = query === raw;

  const result = useQuery({
    queryKey: ["search", "global", query],
    enabled,
    // The previous hits stay listed while the next query runs, so the list does
    // not blink empty between keystrokes.
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    // The rows, and only the rows: `searching` and `debouncedQuery` describe the
    // state of the box rather than the answer, so they are not cached with it.
    queryFn: async (): Promise<Omit<GlobalSearchResults, "searching" | "debouncedQuery">> => {
      const params = new URLSearchParams({ q: query, limit: "5" });
      const qs = params.toString();

      const [invoices, quotations, challans, creditNotes, customers, items] = await Promise.all([
        api.get<InvoiceListResponse>(`${endpoints.invoices}?${qs}`).catch(() => null),
        api.get<QuotationListResponse>(`${endpoints.quotations}?${qs}`).catch(() => null),
        api.get<ChallanListResponse>(`${endpoints.challans}?${qs}`).catch(() => null),
        /*
         * §17.3, and the `credit_notes` FLAG is handled with NOTHING here, the
         * same way the `challans` flag is one line up. Every credit-note route
         * answers 404 while the module is off (§18.3), the per-request
         * `.catch(() => null)` swallows it, and the section simply has no rows
         * and is not drawn. The flag ships DISABLED, so that is today's ordinary
         * path rather than an edge case.
         */
        api.get<CreditNoteListResponse>(`${endpoints.creditNotes}?${qs}`).catch(() => null),
        api.get<CustomerListResponse>(`${endpoints.customers}?${qs}`).catch(() => null),
        api.get<ItemListResponse>(`${endpoints.items}?${qs}`).catch(() => null),
      ]);

      return {
        invoices: (invoices?.invoices ?? []).map((row) => ({
          kind: "invoice" as const,
          id: row.id,
          title: row.number,
          subtitle: row.customerName ?? "",
        })),
        quotations: (quotations?.data ?? []).map((row) => ({
          kind: "quotation" as const,
          id: row.id,
          title: row.number,
          subtitle: row.customerName === "" ? (row.subject ?? "") : row.customerName,
        })),
        challans: (challans?.data ?? []).map((row) => ({
          kind: "challan" as const,
          id: row.id,
          /*
           * `customerName` is the EMPTY STRING, never null, when the frozen
           * snapshot is unreadable — that is the quotation convention and
           * `toChallanSummary` follows it, so `?? …` would never fire and would
           * leave a blank second line. Fall back to the lorry, which is the
           * other thing `q` matched on and the thing the gate remembers.
           */
          title: row.number,
          subtitle: row.customerName === "" ? (row.vehicleNumber ?? "") : row.customerName,
        })),
        creditNotes: (creditNotes?.data ?? []).map((row) => ({
          kind: "creditNote" as const,
          id: row.id,
          title: row.number,
          /*
           * THE BILL IT REDUCES, not the customer name — and this is the one
           * subtitle in the palette that is not "who". §34's link is what makes
           * a credit note readable at all, and `q` matched on that very field, so
           * a row that came back for "26-27/67" must SHOW "26-27/67" or the hit
           * looks unrelated to what was typed.
           *
           * `customerName` is `null` — not `""` — when the frozen snapshot is
           * unreadable here, because `toCreditNoteSummary` follows the INVOICE
           * convention rather than the quotation/challan one. It is not used, but
           * do not "fix" a `?? ""` onto it later thinking it is the same shape as
           * the challan row above.
           */
          subtitle: row.originalInvoiceNumber,
        })),
        customers: (customers?.data ?? []).map((row) => ({
          kind: "customer" as const,
          id: row.id,
          title: row.name,
          subtitle: row.gstin ?? `${row.stateName} (${row.stateCode})`,
        })),
        items: (items?.items ?? []).map((row) => ({
          kind: "item" as const,
          id: row.id,
          title: row.name,
          subtitle: `HSN ${row.hsn}`,
        })),
      };
    },
  });

  return {
    invoices: result.data?.invoices ?? [],
    quotations: result.data?.quotations ?? [],
    challans: result.data?.challans ?? [],
    creditNotes: result.data?.creditNotes ?? [],
    customers: result.data?.customers ?? [],
    items: result.data?.items ?? [],
    debouncedQuery: query,
    /*
     * Searching from the moment there is enough text to search FOR, not from
     * the moment the request leaves — the debounce window belongs to the user's
     * "it is working on it", not to a premature "nothing found" (see `settled`).
     *
     * `isFetching`, not `isPending`: with `keepPreviousData` the query is never
     * pending after the first search, and the spinner would never show again.
     */
    searching: raw.length >= MIN_SEARCH_LENGTH && (!settled || result.isFetching),
  };
}
